import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Switch, Radio, RadioGroup, Typography, Alert, CircularProgress, Grid,
  InputAdornment, Divider, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { countSmsParts, previewSms } from "../Campaign/utils/smsParts";
import {
  aiCallApi, useMarketingPresets, presetOptionText, LANDINGS, formatBdt, formatNumber,
  isObjectId, isoToDhakaInput, dhakaInputToIso
} from "./useAICalling";

const FALLBACK = { holdoutPercent: 10, attributionDays: 7 };

const emptyForm = (marketing, hasPresets) => {
  const holdoutOff = marketing?.features?.holdout === false;
  return {
    name: "",
    description: "",
    audienceMode: hasPresets ? "preset" : "selected",
    preset: "",
    presetOptions: {},
    idsText: "",
    holdoutPercent: String(holdoutOff ? 0 : marketing?.holdoutPercent ?? FALLBACK.holdoutPercent),
    attributionDays: String(marketing?.attributionDays ?? FALLBACK.attributionDays),
    landing: "checkoutProfile",
    startAt: "",
    pilotEnabled: false,
    smsText: "",
  };
};

const toStrings = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === undefined || v === null ? "" : String(v)]));

const formFromCampaign = (c, presets) => {
  const preset = c.audience?.preset || "";
  const defaults = presets.find((p) => p.key === preset)?.options || {};
  return {
    name: c.name || "",
    description: c.description || "",
    audienceMode: preset ? "preset" : "selected",
    preset,
    presetOptions: toStrings({ ...defaults, ...(c.audience?.presetOptions || {}) }),
    idsText: (c.audience?.selectedUserIds || []).join("\n"),
    holdoutPercent: String(c.holdoutPercent ?? 0),
    attributionDays: String(c.attributionDays ?? FALLBACK.attributionDays),
    landing: c.landing || "checkoutProfile",
    startAt: isoToDhakaInput(c.startAt),
    pilotEnabled: !!c.pilot?.enabled,
    smsText: c.pilot?.smsText || "",
  };
};

/** Member IDs typed one per line (commas and spaces also work). */
const parseIds = (text) => {
  const tokens = String(text || "").split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
  const valid = [...new Set(tokens.filter(isObjectId))];
  const invalid = tokens.filter((t) => !isObjectId(t));
  return { valid, invalid };
};

const isWhole = (v, min, max) => v !== "" && Number.isInteger(Number(v)) && Number(v) >= min && Number(v) <= max;

/**
 * New / edit AI call campaign (drafts only).
 * Props: open, onClose, campaign (a draft to edit, or null for a new one), onSaved(campaign)
 */
const AICallCampaignForm = ({ open, onClose, campaign, onSaved }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const marketing = useMarketingPresets();
  const { loaded, presets, settings: marketingSettings, error: marketingError, fetch: fetchMarketing } = marketing;

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const isEdit = !!campaign?._id;

  // load presets and defaults the first time the dialog opens
  useEffect(() => {
    if (open && !loaded) fetchMarketing();
  }, [open, loaded, fetchMarketing]);

  // fill the form once each time the dialog opens (a parent refresh mustn't wipe what was typed)
  const filledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      filledRef.current = false;
      setForm(null);
      setPreview(null);
      return;
    }
    if (!loaded || filledRef.current) return;
    filledRef.current = true;
    setForm(isEdit ? formFromCampaign(campaign, presets) : emptyForm(marketingSettings, presets.length > 0));
  }, [open, loaded, isEdit, campaign, presets, marketingSettings]);

  const holdoutOff = marketingSettings?.features?.holdout === false;
  const linkStyle = marketingSettings?.shortLinkStyle || "full";
  const presetInfo = useMemo(() => presets.find((p) => p.key === form?.preset) || null, [presets, form?.preset]);
  const ids = useMemo(() => parseIds(form?.idsText), [form?.idsText]);
  const sms = useMemo(() => countSmsParts(previewSms(form?.smsText || "", linkStyle)), [form?.smsText, linkStyle]);

  // any change to who is called makes the preview stale
  const set = (field, resetPreview = false) => (e) => {
    const value = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (resetPreview) setPreview(null);
  };

  const choosePreset = (e) => {
    const key = e.target.value;
    const p = presets.find((x) => x.key === key);
    setForm((f) => ({ ...f, preset: key, presetOptions: toStrings(p?.options || {}) }));
    setPreview(null);
  };

  const setOption = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, presetOptions: { ...f.presetOptions, [key]: value } }));
    setPreview(null);
  };

  const audience = () => {
    if (form.audienceMode === "preset") {
      const presetOptions = {};
      for (const [k, v] of Object.entries(form.presetOptions || {})) {
        if (v !== "" && Number.isFinite(Number(v))) presetOptions[k] = Number(v);
      }
      return { preset: form.preset, presetOptions };
    }
    return { selectedUserIds: ids.valid };
  };

  const validateAudience = () => {
    if (form.audienceMode === "preset") {
      if (!form.preset) return "Pick an audience preset";
      for (const [k, v] of Object.entries(form.presetOptions || {})) {
        if (v === "") continue;
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) return `${presetOptionText(form.preset, k).label}: enter 0 or more`;
      }
    } else {
      if (!ids.valid.length) return "Enter at least one member ID";
      if (ids.invalid.length) return `${ids.invalid.length} line${ids.invalid.length === 1 ? " isn't a" : "s aren't"} member ID${ids.invalid.length === 1 ? "" : "s"}`;
    }
    if (!form.pilotEnabled && !holdoutOff && !isWhole(form.holdoutPercent, 0, 50)) return "Holdout must be a whole number from 0 to 50";
    if (form.pilotEnabled) {
      if (!form.smsText.trim()) return "Enter the pilot SMS text";
      if (form.smsText.trim().length > 160) return "Pilot SMS must be 160 characters or less";
    }
    return null;
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    const a = validateAudience();
    if (a) return a;
    if (!isWhole(form.attributionDays, 1, 30)) return "Count payments within 1–30 days";
    if (form.startAt && !dhakaInputToIso(form.startAt)) return "Start time is not valid";
    return null;
  };

  const holdoutValue = () => (holdoutOff ? 0 : Number(form.holdoutPercent) || 0);
  const pilotValue = () => ({ enabled: !!form.pilotEnabled, smsText: form.smsText.trim() });

  const runPreview = async () => {
    const err = validateAudience();
    if (err) return toast.error(err);
    setPreviewing(true);
    const res = await aiCallApi.previewCampaign({ audience: audience(), holdoutPercent: holdoutValue(), pilot: pilotValue() });
    setPreviewing(false);
    if (res.ok) setPreview(res.data);
    else toast.error(res.error);
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      audience: audience(),
      holdoutPercent: holdoutValue(),
      attributionDays: Number(form.attributionDays),
      landing: form.landing,
      startAt: form.startAt ? dhakaInputToIso(form.startAt) : null,
      pilot: pilotValue(),
    };
    setSaving(true);
    const res = isEdit ? await aiCallApi.updateCampaign(campaign._id, payload) : await aiCallApi.createCampaign(payload);
    setSaving(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || (isEdit ? "Campaign saved" : "Campaign created"));
    if (onSaved) onSaved(res.data);
  };

  const busy = saving || previewing;
  const smsLength = form?.smsText.length || 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth fullScreen={fullScreen} scroll="paper">
      <DialogTitle>{isEdit ? "Edit campaign" : "New AI call campaign"}</DialogTitle>
      <DialogContent dividers>
        {!form ? (
          <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>
        ) : (
          <Box>
            {marketingError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Couldn't load the audience presets ({marketingError}). You can still use hand-picked members.
              </Alert>
            )}

            <TextField label="Name" fullWidth required margin="dense" value={form.name} onChange={set("name")} inputProps={{ maxLength: 120 }} />
            <TextField label="Description (optional)" fullWidth margin="dense" multiline minRows={2} value={form.description} onChange={set("description")} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Who to call</Typography>
            <RadioGroup row value={form.audienceMode} onChange={set("audienceMode", true)}>
              <FormControlLabel value="preset" control={<Radio />} label="Audience preset" disabled={!presets.length} />
              <FormControlLabel value="selected" control={<Radio />} label="Hand-picked members" />
            </RadioGroup>

            {form.audienceMode === "preset" ? (
              <Box>
                <TextField select label="Preset" fullWidth margin="dense" value={form.preset} onChange={choosePreset}>
                  {presets.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                </TextField>
                {presetInfo && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>{presetInfo.description}</Typography>
                    <Grid container spacing={2}>
                      {Object.keys(presetInfo.options || {}).map((key) => {
                        const t = presetOptionText(form.preset, key);
                        return (
                          <Grid item xs={12} sm={4} key={key}>
                            <TextField
                              label={t.label}
                              type="number"
                              fullWidth
                              size="small"
                              value={form.presetOptions[key] ?? ""}
                              onChange={setOption(key)}
                              helperText={t.help || `Default ${presetInfo.options[key]}`}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
              </Box>
            ) : (
              <TextField
                label="Member IDs (one per line)"
                fullWidth
                margin="dense"
                multiline
                minRows={4}
                maxRows={10}
                value={form.idsText}
                onChange={set("idsText", true)}
                error={ids.invalid.length > 0}
                helperText={
                  `${formatNumber(ids.valid.length)} member ID${ids.valid.length === 1 ? "" : "s"}` +
                  (ids.invalid.length ? ` · ${ids.invalid.length} line${ids.invalid.length === 1 ? " isn't a" : "s aren't"} member ID${ids.invalid.length === 1 ? "" : "s"}` : "")
                }
                inputProps={{ style: { fontFamily: "monospace" } }}
              />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Only active members with a valid mobile number are included. Safety rules (do-not-call, called recently, opted out,
              call windows, daily budget) are checked again right before each call.
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Pilot</Typography>
            <FormControlLabel
              control={<Switch checked={form.pilotEnabled} onChange={set("pilotEnabled", true)} />}
              label="Run as a pilot (holdout / SMS only / SMS + AI call)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A pilot splits the audience into three equal random groups: a holdout that gets nothing, a group that only gets
              the SMS, and a group that gets the SMS and then an AI call. The SMS is sent at launch (or in the next SMS send
              window); calls start at least 2 hours later, inside the call windows. The Reports page compares the three groups.
            </Typography>
            {form.pilotEnabled && (
              <TextField
                label="Pilot SMS"
                fullWidth
                margin="dense"
                multiline
                minRows={3}
                value={form.smsText}
                onChange={set("smsText")}
                inputProps={{ maxLength: 160, lang: "bn" }}
                error={smsLength > 160}
                helperText={
                  `${smsLength}/160 characters · ${sms.parts} SMS part${sms.parts === 1 ? "" : "s"} per person (${sms.encoding}, with the link)` +
                  ". Put {TrackingLink} where the payment link should go."
                }
              />
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Measuring and link</Typography>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Holdout (%)"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.pilotEnabled ? "" : holdoutOff ? "0" : form.holdoutPercent}
                  onChange={set("holdoutPercent", true)}
                  disabled={form.pilotEnabled || holdoutOff}
                  inputProps={{ min: 0, max: 50, step: 1 }}
                  helperText={
                    form.pilotEnabled
                      ? "The pilot uses three equal groups instead"
                      : holdoutOff
                        ? "Holdout is switched off in Marketing settings"
                        : "Share of the audience that isn't called, to compare against (0–50)"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Count payments within (days)"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.attributionDays}
                  onChange={set("attributionDays")}
                  inputProps={{ min: 1, max: 30, step: 1 }}
                  helperText="A payment counts for this campaign if it happens within this many days (1–30)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Payment link opens" fullWidth size="small" value={form.landing} onChange={set("landing")}
                  helperText="Where the link the agent sends by SMS takes the member">
                  {Object.entries(LANDINGS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Don't call before (optional)"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={form.startAt}
                  onChange={set("startAt")}
                  InputLabelProps={{ shrink: true }}
                  InputProps={form.startAt ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setForm((f) => ({ ...f, startAt: "" }))}>Clear</Button>
                      </InputAdornment>
                    ),
                  } : undefined}
                  helperText="Bangladesh time. Empty = start calling when launched."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={runPreview} disabled={busy}
                startIcon={previewing ? <CircularProgress size={16} /> : undefined}>
                {previewing ? "Counting…" : "Preview"}
              </Button>
              <Typography variant="body2" color="text.secondary">See how many members this reaches and what it can cost.</Typography>
            </Box>
            {preview && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <b>{formatNumber(preview.members)}</b> member{preview.members === 1 ? "" : "s"} ({formatNumber(preview.women)} women)
                  {" · "}<b>{formatNumber(preview.toCall)}</b> to call
                  {form.pilotEnabled
                    ? ` · about ${formatNumber(Math.floor(preview.members / 3))} holdout and ${formatNumber(Math.floor(preview.members / 3))} SMS only`
                    : preview.members - preview.toCall > 0 ? ` · ${formatNumber(preview.members - preview.toCall)} holdout` : ""}
                </Typography>
                <Typography variant="body2">
                  About {formatBdt(preview.perAnsweredCallBdt)} per answered call · at most {formatBdt(preview.maxBdt)} if everyone answers.
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  The daily budget still applies, and members who fail a safety rule are skipped, so the real numbers are lower.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!form || busy}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {isEdit ? "Save" : "Create draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AICallCampaignForm;
